import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";

// --- Timer detection ---
function detectTimer(text) {
  const regex = /(\d+)\s*(minutes?|mins?|hours?|hrs?|seconds?|secs?)/gi;
  const match = regex.exec(text);
  if (!match) return null;
  const value = parseInt(match[1]);
  const unit = match[2].toLowerCase();
  if (unit.startsWith("hour") || unit.startsWith("hr")) return value * 3600;
  if (unit.startsWith("min")) return value * 60;
  if (unit.startsWith("sec")) return value;
  return null;
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function buildTimerAlert(stepIndex, stepText, type) {
  const preview = stepText.length > 72 ? `${stepText.slice(0, 72)}...` : stepText;
  return {
    id: `${type}-${stepIndex}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    stepIndex,
    type,
    title:
      type === "warning"
        ? `Step ${stepIndex + 1} timer ends in 10 seconds`
        : `Step ${stepIndex + 1} timer finished`,
    preview,
  };
}

// --- Keyword-to-animation mapping ---
const COOKING_ANIMATIONS = [
  { keywords: ["mix", "stir", "whisk", "beat", "combine", "fold", "toss"], icon: "\uD83E\uDD44", anim: "cooking-spin-slow" },
  { keywords: ["bake", "oven", "roast"], icon: "\uD83D\uDD25", anim: "cooking-pulse-glow" },
  { keywords: ["boil", "simmer", "steam"], icon: "\uD83C\uDF72", anim: "cooking-bubble" },
  { keywords: ["chop", "dice", "slice", "cut", "mince"], icon: "\uD83D\uDD2A", anim: "cooking-chop" },
  { keywords: ["fry", "saut\u00e9", "saute", "sear", "grill", "pan"], icon: "\uD83C\uDF73", anim: "cooking-sizzle" },
  { keywords: ["pour", "drizzle"], icon: "\uD83E\uDED7", anim: "cooking-pour" },
  { keywords: ["marinate", "soak", "rest", "chill", "refrigerate", "cool"], icon: "\u23F3", anim: "cooking-pulse" },
  { keywords: ["serve", "plate", "garnish", "top", "assemble"], icon: "\uD83C\uDF7D\uFE0F", anim: "cooking-bounce" },
];

function detectAnimation(text) {
  const lower = text.toLowerCase();
  for (const entry of COOKING_ANIMATIONS) {
    if (entry.keywords.some((kw) => lower.includes(kw))) return entry;
  }
  return { icon: "\uD83D\uDC68\u200D\uD83C\uDF73", anim: "cooking-pulse" };
}

// --- Confetti ---
function Confetti() {
  const colors = ["#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6", "#ec4899"];
  return (
    <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden">
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          className="absolute cooking-confetti"
          style={{
            left: `${Math.random() * 100}%`,
            top: `-${Math.random() * 20}px`,
            width: `${8 + Math.random() * 8}px`,
            height: `${8 + Math.random() * 8}px`,
            backgroundColor: colors[i % colors.length],
            borderRadius: Math.random() > 0.5 ? "50%" : "2px",
            animationDelay: `${Math.random() * 0.5}s`,
            animationDuration: `${1.5 + Math.random() * 1.5}s`,
          }}
        />
      ))}
    </div>
  );
}

// --- Circular Timer ---
function CircularTimer({ remaining, total }) {
  const progress = total > 0 ? remaining / total : 0;
  const offset = 283 * (1 - progress);
  const isLow = remaining <= 10 && remaining > 0;

  return (
    <div className="relative w-32 h-32 mx-auto">
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="4" className="text-gray-200 dark:text-gray-700" />
        <circle
          cx="50" cy="50" r="45" fill="none"
          stroke="currentColor" strokeWidth="6"
          strokeDasharray="283" strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          className={`transition-all duration-1000 ${isLow ? "text-red-500" : "text-blue-500"}`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-xl font-bold ${isLow ? "text-red-500 cooking-pulse-glow" : "text-gray-700 dark:text-gray-200"}`}>
          {formatTime(remaining)}
        </span>
      </div>
    </div>
  );
}

// --- Main Component ---
export default function CookingMode({ recipe, onClose }) {
  const steps = useMemo(() => recipe.instructions || [], [recipe.instructions]);
  const ingredients = useMemo(
    () => recipe.recipe_ingredients || [],
    [recipe.recipe_ingredients]
  );
  const totalSteps = steps.length;

  const [currentStep, setCurrentStep] = useState(0);
  const [slideDir, setSlideDir] = useState("");
  // Per-step timers: { [stepIndex]: { total, remaining, running } }
  const [timers, setTimers] = useState({});
  const [showIngredients, setShowIngredients] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [timerAlerts, setTimerAlerts] = useState([]);
  const [pantryDecrementResult, setPantryDecrementResult] = useState(null); // null | "skipped" | { decremented, removed, not_found }
  const [decrementLoading, setDecrementLoading] = useState(false);
  const timerRef = useRef(null);
  const slideTimerRef = useRef(null);
  const confettiTimerRef = useRef(null);
  const previousTimersRef = useRef({});

  useEffect(() => {
    return () => {
      if (slideTimerRef.current) clearTimeout(slideTimerRef.current);
      if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
    };
  }, []);

  const stepText = steps[currentStep] || "";
  const animation = detectAnimation(stepText);
  const detectedTime = detectTimer(stepText);
  const currentTimer = timers[currentStep] || null;

  // Tick all running timers every second
  useEffect(() => {
    const hasRunning = Object.values(timers).some((t) => t.running && t.remaining > 0);
    if (!hasRunning) {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setTimers((prev) => {
        const next = { ...prev };
        for (const key in next) {
          if (next[key].running && next[key].remaining > 0) {
            const priorRemaining = next[key].remaining;
            const nextRemaining = Math.max(priorRemaining - 1, 0);
            const shouldWarn =
              priorRemaining > 10 &&
              nextRemaining <= 10 &&
              !next[key].warningShown;
            const shouldNotifyDone =
              nextRemaining === 0 && !next[key].doneShown;

            next[key] = {
              ...next[key],
              remaining: nextRemaining,
              running: nextRemaining > 0,
              warningShown: next[key].warningShown || shouldWarn,
              doneShown: next[key].doneShown || shouldNotifyDone,
            };
          }
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timers]);

  useEffect(() => {
    const previousTimers = previousTimersRef.current;
    const queuedAlerts = [];

    Object.entries(timers).forEach(([key, timer]) => {
      const stepIndex = Number(key);
      const previousTimer = previousTimers[key];

      if (timer.warningShown && !previousTimer?.warningShown) {
        queuedAlerts.push(buildTimerAlert(stepIndex, steps[stepIndex] || "", "warning"));
      }

      if (timer.doneShown && !previousTimer?.doneShown) {
        queuedAlerts.push(buildTimerAlert(stepIndex, steps[stepIndex] || "", "done"));
      }
    });

    if (queuedAlerts.length) {
      setTimerAlerts((prev) => [...prev, ...queuedAlerts]);
    }

    previousTimersRef.current = timers;
  }, [timers, steps]);

  const startTimer = () => {
    if (detectedTime) {
      setTimers((prev) => ({
        ...prev,
        [currentStep]: {
          total: detectedTime,
          remaining: detectedTime,
          running: true,
          warningShown: false,
          doneShown: false,
        },
      }));
    }
  };

  const toggleTimerPause = () => {
    setTimers((prev) => ({
      ...prev,
      [currentStep]: { ...prev[currentStep], running: !prev[currentStep]?.running },
    }));
  };

  const dismissTimerAlert = (alertId) => {
    setTimerAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
  };

  const handleDecrementPantry = async () => {
    setDecrementLoading(true);
    try {
      const res = await axiosInstance.post(`/api/recipes/${recipe.id}/cook/`, undefined, {
        _skipGlobalErrorToast: true,
      });
      setPantryDecrementResult(res.data);
    } catch {
      setPantryDecrementResult({ error: true });
    } finally {
      setDecrementLoading(false);
    }
  };

  const handleSkipPantry = () => {
    setPantryDecrementResult("skipped");
  };

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setSlideDir("left");
      slideTimerRef.current = setTimeout(() => {
        setCurrentStep((s) => s + 1);
        setSlideDir("");
      }, 200);
    } else {
      setCompleted(true);
      setShowConfetti(true);
      confettiTimerRef.current = setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [currentStep, totalSteps]);

  const goPrev = useCallback(() => {
    if (currentStep > 0) {
      setSlideDir("right");
      slideTimerRef.current = setTimeout(() => {
        setCurrentStep((s) => s - 1);
        setSlideDir("");
      }, 200);
    }
  }, [currentStep]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); goNext(); }
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [goNext, goPrev, onClose]);

  if (totalSteps === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 max-w-lg w-full text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">No instructions for this recipe.</p>
          <button onClick={onClose} className="bg-blue-500 text-white px-6 py-2 rounded-lg">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] min-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
      {showConfetti && <Confetti />}
      {timerAlerts.length > 0 && (
        <div className="pointer-events-none absolute right-4 top-4 z-[70] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
          {timerAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`pointer-events-auto rounded-xl border px-4 py-3 shadow-lg ${
                alert.type === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/80 dark:text-amber-100"
                  : "border-green-200 bg-green-50 text-green-900 dark:border-green-700 dark:bg-green-900/80 dark:text-green-100"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">{alert.title}</p>
                  <p className="mt-1 text-xs opacity-80">{alert.preview}</p>
                </div>
                <button
                  onClick={() => dismissTimerAlert(alert.id)}
                  className="text-base leading-none opacity-70 transition hover:opacity-100"
                  aria-label={`Dismiss ${alert.title}`}
                >
                  &times;
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-800">
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none">&times;</button>
        <h2 className="text-lg font-bold text-gray-800 dark:text-white truncate mx-4">{recipe.title}</h2>
        {!completed && (
          <span className="text-sm text-gray-400 dark:text-gray-500 whitespace-nowrap">
            Step {currentStep + 1}/{totalSteps}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 overflow-hidden">
        {completed ? (
          <div className="text-center cooking-fade-in max-w-md">
            <div className="text-6xl mb-6 cooking-bounce">&#127881;</div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">You finished cooking!</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8">{recipe.title}</p>

            {pantryDecrementResult === null ? (
              <>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Did you use ingredients from your pantry?
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleSkipPantry}
                    disabled={decrementLoading}
                    className="px-6 py-3 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                  >
                    No, skip
                  </button>
                  <button
                    onClick={handleDecrementPantry}
                    disabled={decrementLoading}
                    className="px-6 py-3 rounded-lg text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 transition"
                  >
                    {decrementLoading ? "Updating..." : "Yes, update pantry"}
                  </button>
                </div>
              </>
            ) : (
              <>
                {pantryDecrementResult !== "skipped" && !pantryDecrementResult.error && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Pantry updated: {pantryDecrementResult.decremented} reduced,
                    {" "}{pantryDecrementResult.removed} removed
                    {pantryDecrementResult.not_found > 0 && `, ${pantryDecrementResult.not_found} not in pantry`}
                  </p>
                )}
                <button
                  onClick={onClose}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-lg text-lg transition"
                >
                  Done
                </button>
              </>
            )}
          </div>
        ) : (
          <div
            className={`w-full max-w-lg text-center transition-all duration-200 ease-in-out ${
              slideDir === "left" ? "-translate-x-full opacity-0" :
              slideDir === "right" ? "translate-x-full opacity-0" :
              "translate-x-0 opacity-100"
            }`}
          >
            {/* Animated icon */}
            <div className={`text-5xl mb-6 ${animation.anim}`}>{animation.icon}</div>

            {/* Instruction text */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl px-8 py-6 mb-6 shadow-sm">
              <p className="text-lg text-gray-700 dark:text-gray-200 leading-relaxed">{stepText}</p>
            </div>

            {/* Timer */}
            {detectedTime && (
              <div className="mb-6">
                {currentTimer ? (
                  <>
                    <CircularTimer remaining={currentTimer.remaining} total={currentTimer.total} />
                    <div className="mt-3">
                      {currentTimer.running ? (
                        <button onClick={toggleTimerPause} className="text-sm text-red-500 hover:text-red-600 transition">
                          Pause
                        </button>
                      ) : currentTimer.remaining > 0 ? (
                        <button onClick={toggleTimerPause} className="text-sm text-blue-500 hover:text-blue-600 transition">
                          Resume
                        </button>
                      ) : (
                        <p className="text-sm font-semibold text-green-500 cooking-bounce">Timer done!</p>
                      )}
                    </div>
                  </>
                ) : (
                  <button
                    onClick={startTimer}
                    className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-medium px-5 py-2.5 rounded-lg text-sm transition"
                  >
                    &#9201; Start Timer ({formatTime(detectedTime)})
                  </button>
                )}
              </div>
            )}

            {/* Progress bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {!completed && (
        <div className="px-6 pb-6 pt-2">
          <div className="flex items-center justify-between max-w-lg mx-auto mb-4">
            <button
              onClick={goPrev}
              disabled={currentStep === 0}
              className="px-6 py-3 rounded-lg text-sm font-medium transition disabled:opacity-30
                bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              Previous
            </button>
            <button
              onClick={goNext}
              className="px-6 py-3 rounded-lg text-sm font-medium transition bg-blue-500 hover:bg-blue-600 text-white"
            >
              {currentStep === totalSteps - 1 ? "Finish" : "Next"}
            </button>
          </div>

          <div className="max-w-lg mx-auto">
            <button
              onClick={() => setShowIngredients(!showIngredients)}
              className="w-full text-center text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
            >
              {showIngredients ? "Hide ingredients" : `Show ingredients (${ingredients.length})`}
            </button>
            {showIngredients && (
              <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-xl p-4 cooking-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {ingredients.map((ing) => (
                    <p key={ing.id} className="text-sm text-gray-600 dark:text-gray-300 py-0.5">
                      <span className="text-gray-400 mr-1">&bull;</span>
                      {ing.original_text}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
